import React, { useEffect, useRef, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearSelectedClientIdsAction,
  clearSelectedScheduleAction,
  clearSelectedStatusAction,
  scheduleState,
  setSelectedClientIdsListWithClearAction,
  setSelectedScheduleAction,
  setSelectedStatusAction,
} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import { DeliveryInGroup } from "../../types/schedule/delivery-in-group";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import CloseLineIcon from "../icons/CloseLineIcon/CloseLineIcon";
import SearchLineIcon from "../icons/SearchLineIcon/SearchLineIcon";
import { EMPTY_LINE_NAME, NOT_ASSIGNED_SCHEDULE } from "../../utils/static/schedule-static";

interface SearchScheduleProps {
  getScheduleId?: (scheduleId?: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const SearchDeliveryAutocomplete: React.FC<SearchScheduleProps> = (
  {
    getScheduleId,
    inputRef,
    ...rest
  }
) => {
  const dispatch = useAppDispatch();
  const { schedulePageAll, selectedSchedule, selectedStatus } =
    useAppSelector(scheduleState);

  const inputTagRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [openSearchDropdown, setOpenSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<DeliveryInGroup[]>([]);
  const [selectScheduleResult, setSelectScheduleResult] =
    useState<DeliveryInGroup>();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const scheduleCache = useRef<Map<string, any>>(new Map());
  const allDeliveriesRef = useRef<DeliveryInGroup[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const filteredScheduleBySelectedStatus = schedulePageAll.contents.filter(
    (schedule) => {
      const status =
        selectedStatus.length > 0
          ? selectedStatus
          : ["WAIT", "WORK", "COMPLETED"];
      return status.includes(schedule.status);
    }
  );

  const filteredDeliveries = filteredScheduleBySelectedStatus.flatMap(
    (schedule) => {
      return schedule.deliveriesInGroup;
    }
  );

  useEffect(() => {
    allDeliveriesRef.current = filteredDeliveries;
    scheduleCache.current.clear();
    filteredScheduleBySelectedStatus.forEach(schedule => {
      scheduleCache.current.set(schedule.id, schedule);
    });
  }, [schedulePageAll, selectedStatus]);

  const handleClickSelectScheduleResult = (result: DeliveryInGroup) => {
    setSelectScheduleResult(result);
  };

  const handleClickSelectedScheduleCloseButton = (
    fromSearch: boolean,
  ) => {
    setSearchKeyword("");
    setSearchResults([]);
    setSelectScheduleResult(undefined);
    // inputTagRef && inputTagRef?.current?.focus();
    getScheduleId?.();
    if (fromSearch) {
      dispatch(clearSelectedStatusAction());
      dispatch(clearSelectedScheduleAction());
      dispatch(clearSelectedClientIdsAction());
    }
  };

  const handleClickSelectedSchedule = () => {
    setOpenSearchDropdown(true);
    setSelectScheduleResult(undefined);

    // inputTagRef && inputTagRef.current?.focus();
  };

  const handleFocusOutOutside = (e?: any) => {
    if (openSearchDropdown && !inputTagRef?.current?.contains(e.target)) {
      setOpenSearchDropdown(false);
    }
  };

  const searchKeywordFilter = (delivery: DeliveryInGroup) => {
    if (!searchKeyword.trim()) return true;

    const keyword = searchKeyword.toLowerCase().trim();

    // 캐싱된 스케줄 정보 활용
    const scheduleId = delivery.scheduleId;
    const schedule = scheduleCache.current.get(scheduleId);

    if (!schedule) return false;

    return (
      // 라인명 검색
      (schedule.alias || '').toLowerCase().includes(keyword) ||
      // 프로 이름 검색
      (schedule.worker?.name || '').toLowerCase().includes(keyword) ||
      // 상차지 검색
      (delivery.inProgressWorkPlace.alias || '').toLowerCase().includes(keyword) ||
      // 전달지 검색
      (delivery.pairWorkPlace.alias || '').toLowerCase().includes(keyword)
    );
  };

  const searchScheduleByDeliveryAndAlias = () => {
    const deliveryInSearchSchedule =
      allDeliveriesRef.current.filter(searchKeywordFilter);

    setSearchResults(deliveryInSearchSchedule);
    setOpenSearchDropdown(deliveryInSearchSchedule.length > 0);
  };

  // 디바운스된 검색 실행 함수
  const debouncedSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setOpenSearchDropdown(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchScheduleByDeliveryAndAlias();
    }, 100); // 100ms 디바운스
  };

  // 검색어 변경 핸들러
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKeyword(value);

    const nativeEvent = e.nativeEvent as InputEvent;

    // 백스페이스로 지우는 경우 즉시 검색
    if (value.length < searchKeyword.length) {
      searchScheduleByDeliveryAndAlias();
      return;
    }

    // 한글 입력 중이 아닐 때만 디바운스 검색 실행
    if (!nativeEvent.isComposing) {
      debouncedSearch(value);
    }
  };

  // cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener("click", handleFocusOutOutside);
    return () => {
      window.removeEventListener("click", handleFocusOutOutside);
    };
  }, [openSearchDropdown]);

  useEffect(() => {
    if (!selectedSchedule) {
      handleClickSelectedScheduleCloseButton(false);
    }
  }, [selectedSchedule]);

  const scrollToItem = (index: number) => {
    const menuList = menuListRef.current;
    const menuItem = menuList?.children[index] as HTMLElement;

    if (menuList && menuItem) {
      const menuListRect = menuList.getBoundingClientRect();
      const menuItemRect = menuItem.getBoundingClientRect();

      if (menuItemRect.bottom > menuListRect.bottom) {
        menuList.scrollTop += menuItemRect.bottom - menuListRect.bottom;
      } else if (menuItemRect.top < menuListRect.top) {
        menuList.scrollTop -= menuListRect.top - menuItemRect.top;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchResults.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : prev;
          scrollToItem(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : prev;
          scrollToItem(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          const delivery = searchResults[selectedIndex];
          const schedule = schedulePageAll.contents.find(
            (schedule) => schedule.id === delivery.scheduleId
          );
          if (schedule) {
            handleClickSelectScheduleResult(delivery);
            dispatch(
              setSelectedStatusAction({
                selectedStatus: schedule.status,
              })
            );
            dispatch(
              setSelectedClientIdsListWithClearAction({
                selectedClientIds: schedule.clientIds,
              })
            );
            dispatch(
              setSelectedScheduleAction({
                selectedSchedule: schedule,
              })
            );
            setOpenSearchDropdown(false);
            setSelectedIndex(-1);
          }
        }
        break;
      case 'Escape':
        event.preventDefault();
        setOpenSearchDropdown(false);
        setSelectedIndex(-1);
        setSearchKeyword("");
        setSearchResults([]);
        setSelectScheduleResult(undefined);
        handleClickSelectedScheduleCloseButton(true);
        break;
    }
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <SearchContainer>
          <SearchIconBox>
            <SearchLineIcon />
          </SearchIconBox>

          <SearchInputRoot
            onClick={() => {
              setOpenSearchDropdown(true);
            }}
            ref={inputRef || inputTagRef}
            {...rest}
            onChange={handleSearchInputChange}
            onCompositionEnd={(e) => {
              // 한글 조합이 완전히 끝났을 때 즉시 검색 실행
              const value = e.currentTarget.value;
              if (value.trim()) {
                searchScheduleByDeliveryAndAlias();
              }
            }}
            onKeyDown={handleKeyDown}
            value={searchKeyword}
            placeholder={"라인명 | 프로님 | 상차지 | 전달지를 검색하세요"}
          />

          {selectScheduleResult?.scheduleId && (
            <SearchResult onClick={handleClickSelectedSchedule}>
              <SearchResultContainer>
                <SearchResultItem className="line-name">
                  {selectScheduleResult.scheduleAlias || EMPTY_LINE_NAME}
                </SearchResultItem>
                <SearchResultItem className="pro-name">
                  {selectScheduleResult.worker?.name || NOT_ASSIGNED_SCHEDULE}
                </SearchResultItem>
                <SearchResultItem className="pick-up-location">
                  {selectScheduleResult.inProgressWorkPlace.alias}
                </SearchResultItem>
                <SearchResultItem className="drop-off-location">
                  {selectScheduleResult.pairWorkPlace.alias}
                </SearchResultItem>

                <SearchCloseBtn
                  startIcon={<CloseLineIcon />}
                  size="circle"
                  color="iconOnly"
                  onClick={() => handleClickSelectedScheduleCloseButton(true)}
                />
              </SearchResultContainer>
            </SearchResult>
          )}

          {searchResults.length > 0 && openSearchDropdown && (
            <DropDown>
              <DropDownContainer>
                <DropDownHeader>
                  <DropDownColumn className="line-name">
                    {"라인명"}
                  </DropDownColumn>
                  <DropDownColumn className="pro-name">
                    {"프로이름"}
                  </DropDownColumn>
                  <DropDownColumn className="pick-up-location">
                    {"상차지"}
                  </DropDownColumn>
                  <DropDownColumn className="drop-off-location">
                    {"전달지"}
                  </DropDownColumn>
                </DropDownHeader>

                <RenderResultsBox ref={menuListRef}>
                  {searchResults &&
                    searchResults.map((delivery: DeliveryInGroup, index: number) => {
                      const schedule = schedulePageAll.contents.find(
                        (schedule) => schedule.id === delivery.scheduleId
                      );
                      if (!schedule) {
                        return null;
                      }
                      return (
                        <RenderResultContainer
                          key={`${delivery.scheduleId}-${delivery.inProgressWorkPlace.alias}-${index}`}
                          className={`${searchResults.length > 19 && "has-scroll-box"
                            } result-container`}
                          selected={index === selectedIndex}
                          onClick={() => {
                            handleClickSelectScheduleResult(delivery);
                            dispatch(
                              setSelectedStatusAction({
                                selectedStatus: schedule.status,
                              })
                            );
                            dispatch(
                              setSelectedClientIdsListWithClearAction({
                                selectedClientIds: schedule.clientIds,
                              })
                            );
                            dispatch(
                              setSelectedScheduleAction({
                                selectedSchedule: schedule,
                              })
                            );
                          }}
                        >
                          <RenderResultItem className="line-name result-item">
                            {schedule.alias || ""}
                          </RenderResultItem>
                          <RenderResultItem className="pro-name result-item">
                            {schedule.worker?.name || ""}
                          </RenderResultItem>
                          <RenderResultItem className="pick-up-location result-item">
                            {delivery.inProgressWorkPlace.alias}
                          </RenderResultItem>
                          <RenderResultItem className="drop-off-location result-item">
                            {delivery.pairWorkPlace.alias}
                          </RenderResultItem>
                        </RenderResultContainer>
                      );
                    })}
                </RenderResultsBox>
              </DropDownContainer>
            </DropDown>
          )}
        </SearchContainer>
      </ThemeProvider>
    </React.Fragment>
  );
};

const SearchContainer = styled.div`
  max-width: 608px;
  min-width: 335px;
  position: relative;
  padding: 0px;
  box-sizing: border-box;
  flex-grow: 1;
`;

const BorderLine = styled.div`
  width: 1px;
  height: 26px;
  background: ${(props: any) => props.theme.palette.primitives.gray200};
`;

const SearchIconBox = styled(Icon)`
  position: absolute;
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  top: 10px;
  left: 8px;
  z-index: 1;

  .cMZSwz svg path {
    fill: #4d535c;
  }
`;

const SearchResult = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  // width: 592px;
  width: 100%;
  height: 40px;
  border: 1px solid #fcca00;
  background: #fffcf2;
  border-radius: 4px;
  font-size: 100%;
  margin: 0;
  vertical-align: baseline;
  *vertical-align: middle;
  line-height: normal;
  box-sizing: border-box;
  font-weight: 400;
  box-sizing: border-box;
  padding: 0 8px;
`;

const SearchResultContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding-left: 24px;
  padding-right: 32px;
  box-sizing: border-box;
`;

const SearchCloseBtn = styled(Button)`
  position: absolute;
  top: 9px;
  right: 8px;
  min-width: unset;
  width: 20px;
  height: 20px;
  padding: 2px;
  border-radius: 24px;
  background: ${(props: any) => props.theme.palette.primitives.yellow400};

  & span {
    width: 16px;
    height: 16px;
    padding: 0px;
  }

  & svg {
    padding: 2px;
  }

  & svg path {
    fill: #ffffff;
  }
`;

const SearchResultItem = styled.div`
  position: relative;
  font-weight: 400;
  font-size: 16px;
  text-align: left;
  line-height: 40px;
  box-sizing: border-box;
  padding: 0px 8px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.line-name,
  &.pick-up-location,
  &.drop-off-location {
    flex: 0.8;
  }

  &.pro-name {
    flex: 0.4;
  }

  &::after {
    content: "";
    position: absolute;
    top: 20%; /* (100% - 91%) / 2 = 4.5% */
    height: 60%;
    width: 1px;
    border-bottom: 1px solid #e0e3e9;
    right: 0;
    background: ${(props: any) => props.theme.palette.primitives.gray200};
  }

  &.drop-off-location::after {
    content: none;
  }
`;

const SearchInputRoot = styled.input.attrs((props: any) => ({
  id: props.id,
  name: props.id,
  type: "text",
  disabled: props.disabled,
  checked: props.checked,
}))`
  padding: 0px;
  width: 100%;
  // max-width: ;
  min-width: 335px;
  // width: max-content;
  height: 40px;
  margin: 0;
  vertical-align: baseline;
  vertical-align: middle;

  box-sizing: border-box;
  flex-grow: 1;

  font-weight: 400;
  font-size: 16px;
  line-height: 126.02%;
  outline: none;

  background-color: #f5f6f9;

  border-radius: 4px;
  border: 1px solid #e0e3e9;

  text-indent: 32px;

  &::placeholder {
    color: #979da8;
  }

  &[type="text"]:focus {
    background-color: transparent;
    border: 1px solid #fcca00;
  }

  &:focus {
    /* Primary/Yellow */
    border: 1px solid #fcca00;
    border-radius: 4px;
    color: #1c2026;
    background-color: transparent;
  }

  &:active {
    border: 1px solid #fcca00;
  }
`;

const DropDown = styled.div`
  position: absolute;
  left: 0;
  top: 46px;
  z-index: 1;
`;

const DropDownContainer = styled.div`
  width: 608px;
  height: 100%;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.12);
  border-radius: 2px;
  background: #ffffff;

  & .result-container:last-child .result-item {
    border-bottom: 0;
  }
`;

const DropDownHeader = styled.div`
  display: flex;
  width: 608px;
  height: 42px;
  padding: 8px;
  box-sizing: border-box;
  border-bottom: 1px solid #e0e3e9;
  align-items: center;
`;

const RenderResultsBox = styled.div`
  padding: 10px 0px;
  box-sizing: border-box;
  max-height: 717px;
  overflow-y: auto;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #bec3cb;
  }
`;

const DropDownColumn = styled.p`
  position: relative;
  margin: 0;
  height: 100%;
  font-weight: 500;
  font-size: 15px;
  line-height: 26px;
  padding: 0px 8px;
  box-sizing: border-box;
  color: #979da8;

  &.line-name,
  &.pick-up-location,
  &.drop-off-location {
    flex: 1;
  }

  &.pro-name {
    flex: 0.4;
  }

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::after {
    content: "";
    position: absolute;
    top: 10%; /* (100% - 91%) / 2 = 4.5% */
    height: 80%;
    width: 1px;
    border-bottom: 1px solid #e0e3e9;
    right: 0;
    background: ${(props: any) => props.theme.palette.primitives.gray200};
  }

  &.drop-off-location::after {
    content: none;
  }
`;

const RenderResultContainer = styled.div<{ selected?: boolean }>`
  display: flex;
  padding: 0px 8px;
  box-sizing: border-box;
  cursor: pointer;
  background-color: ${props => props.selected ? "#FFF7D9" : "transparent"};

  &:hover {
    background: #f5f6f9;
  }

  &.has-scroll-box {
    padding: 0px 0px 0px 8px;
  }
`;

const RenderResultItem = styled.div`
  font-weight: 400;
  font-size: 14px;
  text-align: left;
  border-bottom: 1px solid ${(props: any) => props.theme.palette.primitives.gray100};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  padding: 10px 8px;

  &.line-name,
  &.pick-up-location,
  &.drop-off-location {
    flex: 1;
  }

  &.pro-name {
    flex: 0.4;
  }
`;
